/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ContentApiService } from '@alfresco/aca-shared';
import { UserAvatarService } from '../branding/user-avatar.service';
import { AppLanguageService } from '../shared/language.service';
import { SetSelectedNodesAction } from '@alfresco/aca-shared/store';
import { LogoutDirective } from '@alfresco/adf-core';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';
import { ThemeService } from './theme.service';

/** The user menu in the top-right of the header: avatar + name, opening a menu
 *  of profile / language / theme / about / sign out.
 *
 *  It is the only user menu in the application. It sat at the foot of the
 *  sidebar while the header carried a language picker; the picker's one job —
 *  switching language — is a row in this menu, so the header shows the identity
 *  the reference design puts there and nothing is offered twice. About lives
 *  here too, or the /about page would have no way in. */
@Component({
  selector: 'app-profile-menu',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatMenuModule, MatDividerModule, MatTooltipModule, MatSlideToggleModule, LogoutDirective, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <button class="aca-userbtn" [matMenuTriggerFor]="profileMenu" [matTooltip]="name()">
      <ng-container *ngTemplateOutlet="avatar" />
      <span class="aca-userbtn__name">{{ name() }}</span>
      <mat-icon class="aca-profile__chev">expand_more</mat-icon>
    </button>

    <mat-menu #profileMenu="matMenu" class="aca-profile-menu" yPosition="below" xPosition="before">
      <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
      <div class="aca-profile-menu__head" (click)="$event.stopPropagation()">
        <ng-container *ngTemplateOutlet="avatar" />
        <div class="aca-profile-menu__id">
          <div class="aca-profile-menu__name">{{ name() }}</div>
          <div class="aca-profile-menu__sub" *ngIf="sub()">{{ sub() }}</div>
        </div>
      </div>
      <button mat-menu-item (click)="openProfile()">
        <mat-icon>account_circle</mat-icon><span>{{ 'DMS.UI.BEEE3B' | translate }}</span>
      </button>
      <button mat-menu-item [matMenuTriggerFor]="langMenu">
        <mat-icon>language</mat-icon><span>{{ 'DMS.UI.0EBB64' | translate }}</span>
      </button>
      <!-- About used to hang off the header's user menu, which this menu now
           replaces; without it here the /about page has no way in. -->
      <button mat-menu-item (click)="openAbout()">
        <mat-icon>info</mat-icon><span>{{ 'DMS.UI.825A03' | translate }}</span>
      </button>
      <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
      <div class="aca-profile-menu__toggle" (click)="$event.stopPropagation()">
        <mat-icon>{{ theme.isDark() ? 'light_mode' : 'dark_mode' }}</mat-icon>
        <span>{{ 'DMS.UI.544A8E' | translate }}</span>
        <mat-slide-toggle
          [checked]="theme.isDark()"
          (change)="theme.toggle()"
          [attr.aria-label]="(theme.isDark() ? 'DMS.UI.2CB6C8' : 'DMS.UI.40C162') | translate"
        />
      </div>
      <mat-divider />
      <button mat-menu-item adf-logout (click)="onLogout()">
        <mat-icon>logout</mat-icon><span>{{ 'DMS.UI.E04FFA' | translate }}</span>
      </button>
    </mat-menu>

    <mat-menu #langMenu="matMenu">
      <button mat-menu-item *ngFor="let option of languages.languages" (click)="languages.use(option.key)">
        <mat-icon *ngIf="option.key === languages.current()">check</mat-icon>
        <span>{{ option.label }}</span>
      </button>
    </mat-menu>

    <!-- the picture the person uploaded, or their initials until they do -->
    <ng-template #avatar>
      <img *ngIf="avatarUrl(); else initialsOnly" class="aca-profile__avatarimg" [src]="avatarUrl()" [alt]="name()" />
      <ng-template #initialsOnly
        ><span class="aca-profile__avatar">{{ initials() }}</span></ng-template
      >
    </ng-template>
  `
})
export class ProfileMenuComponent implements OnInit {
  private readonly content = inject(ContentApiService);
  private readonly avatar = inject(UserAvatarService);
  readonly languages = inject(AppLanguageService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  readonly theme = inject(ThemeService);

  readonly name = signal('User');
  readonly sub = signal('');
  readonly initials = signal('U');
  readonly avatarUrl = this.avatar.url;

  ngOnInit(): void {
    this.avatar.refresh();
    this.content
      .getPerson('-me-')
      .pipe(catchError(() => of(null)))
      .subscribe((res: any) => {
        const e = res?.entry;
        if (!e) {
          return;
        }
        const displayName = e.displayName || e.id || 'User';
        this.name.set(displayName);
        this.sub.set(e.jobTitle || e.email || e.id || '');
        this.initials.set(this.computeInitials(e.firstName, e.lastName, displayName));
      });
  }

  openProfile(): void {
    this.router.navigate(['/profile']);
  }

  openAbout(): void {
    this.router.navigate(['/about']);
  }

  onLogout(): void {
    this.store.dispatch(new SetSelectedNodesAction([]));
  }

  private computeInitials(first?: string, last?: string, displayName = ''): string {
    if (first && last) {
      return (first[0] + last[0]).toUpperCase();
    }
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    const ini = ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
    return ini || (displayName[0] || 'U').toUpperCase();
  }
}
